import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge } from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import api, { getMediaUrl } from '../services/api';
import './ServiceDetail.css';

const ServiceDetail = () => {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadService();
  }, [slug]);

  const loadService = async () => {
    try {
      const response = await api.get(`/services/${slug}/`);
      setService(response.data);
    } catch (error) {
      console.error('Error loading service:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </Container>
    );
  }

  if (!service) {
    return (
      <Container className="py-5">
        <h1>Service Not Found</h1>
        <Link to="/services" className="btn btn-primary">Back to Services</Link>
      </Container>
    );
  }

  return (
    <Container className="service-detail py-5">
      <Row>
        <Col lg={8}>
          {/* Service Header */}
          <div className="service-header mb-4">
            <h1 className="service-title">{service.name}</h1>
            <div className="service-meta">
              {service.department_name && (
                <Badge bg="info" className="me-2">
                  <i className="fas fa-building me-1"></i>
                  {service.department_name}
                </Badge>
              )}
              {service.processing_time && (
                <Badge bg="secondary">
                  <i className="fas fa-clock me-1"></i>
                  {service.processing_time}
                </Badge>
              )}
            </div>
          </div>

          {/* Service Image */}
          {service.image && (
            <div className="service-image-container mb-4">
              <img 
                src={getMediaUrl(service.image)} 
                alt={service.name}
                className="service-detail-image"
              />
              {service.icon && (
                <div className="service-icon-badge">
                  <i className={`${service.icon} fa-2x`}></i>
                </div>
              )}
            </div>
          )}

          {/* Service Description */}
          <div className="service-description mb-4 text-justify">
            <div dangerouslySetInnerHTML={{ __html: service.description }} />
          </div>
          
          {/* How to Apply */}
          {service.how_to_apply && (
            <Card className="service-card mb-4">
              <Card.Header className="bg-primary text-white">
                <h5 className="mb-0">
                  <i className="fas fa-check-circle me-2"></i>
                  How to Apply
                </h5>
              </Card.Header>
              <Card.Body dangerouslySetInnerHTML={{ __html: service.how_to_apply }} />
            </Card>
          )}
          
          {/* Required Documents */}
          {service.required_documents && (
            <Card className="service-card mb-4">
              <Card.Header className="bg-info text-white">
                <h5 className="mb-0">
                  <i className="fas fa-file-alt me-2"></i>
                  Required Documents
                </h5>
              </Card.Header>
              <Card.Body dangerouslySetInnerHTML={{ __html: service.required_documents }} />
            </Card>
          )}
        </Col>

        {/* Sidebar */}
        <Col lg={4}>
          {/* Service Information Card */}
          <Card className="service-info-card mb-4 sticky-top" style={{ top: '20px' }}>
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">
                <i className="fas fa-info-circle me-2"></i>
                Service Information
              </h5>
            </Card.Header>
            <Card.Body>
              {service.fees && (
                <div className="info-item mb-3">
                  <label className="info-label">Fees</label>
                  <p className="info-value">
                    <Badge bg="success">{service.fees}</Badge>
                  </p>
                </div>
              )}

              {service.processing_time && (
                <div className="info-item mb-3">
                  <label className="info-label">
                    <i className="fas fa-hourglass-half me-2"></i>
                    Processing Time
                  </label>
                  <p className="info-value">{service.processing_time}</p>
                </div>
              )}

              {service.department_name && (
                <div className="info-item mb-3">
                  <label className="info-label">
                    <i className="fas fa-building me-2"></i>
                    Department
                  </label>
                  <p className="info-value">{service.department_name}</p>
                </div>
              )}

              {service.online_application_url && (
                <a 
                  href={service.online_application_url} 
                  className="btn btn-primary w-100 mb-2" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <i className="fas fa-external-link-alt me-2"></i>
                  Apply Online
                </a>
              )}

              <Link to="/services" className="btn btn-outline-primary w-100">
                <i className="fas fa-arrow-left me-2"></i>
                Back to Services
              </Link>
            </Card.Body>
          </Card>

          {/* Quick Contact Card */}
          <Card className="quick-contact-card">
            <Card.Header className="bg-success text-white">
              <h5 className="mb-0">
                <i className="fas fa-phone me-2"></i>
                Need Help?
              </h5>
            </Card.Header>
            <Card.Body>
              <p className="small mb-3">If you have any questions about this service, please contact us.</p>
              <a href="tel:support" className="btn btn-success btn-sm w-100 mb-2">
                <i className="fas fa-phone me-2"></i>
                Call Support
              </a>
              <a href="mailto:support@municipality.gov" className="btn btn-info btn-sm w-100">
                <i className="fas fa-envelope me-2"></i>
                Send Email
              </a>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ServiceDetail;

